import {
  Box,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useTheme
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import FilterListIcon from "@material-ui/icons/FilterList";
import { Button, ContainerPage, TitlePage } from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pricingService from "services/pricingService";
import productApiServices from "services/productApiServices";
import { Table } from "shared";

import FilterDrawer from "./components/FilterDrawer";
import { useStyles } from "./styles";

const COMPACT_HEAD = { padding: "6px 8px" };
const compactCell = () => ({ padding: "6px 8px" });

const formatCurrency = value =>
  value != null
    ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "--";

const formatPercent = value => {
  if (value == null) return "--";
  return `${(value * 100).toFixed(1)}%`;
};

const formatQty = value =>
  value != null
    ? value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })
    : "--";

const formatDate = value => {
  if (!value) return "--";
  const [year, month, day] = String(value).split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const PromoSuggestions = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { addPopup } = usePopup();
  const addPopupRef = useRef(addPopup);
  addPopupRef.current = addPopup;

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [editedPromo, setEditedPromo] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const pendingPromoCount = Object.keys(editedPromo).length;

  const { data, params, onChange, status, refresh, handleSearch, count } =
    useTableParams({
      service: p => pricingService.getPromoSuggestions(p),
      paramsDefault: { latest_run: true }
    });

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const { data: result } = await productApiServices.getCategories();
        if (cancelled) return;
        const list = Array.isArray(result) ? result : result?.results ?? [];
        setCategoryOptions(
          list
            .filter(c => c?.name)
            .map(c => ({ id: c.name, label: c.name }))
            .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
        );
      } catch (error) {
        if (cancelled || error?.response?.status === 401) return;
        addPopupRef.current({
          type: "error",
          title: "Erro ao carregar categorias",
          description: error?.response?.data?.msg
        });
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeFiltersCount = [
    activeFilters.run_id,
    activeFilters.reference_date_from,
    activeFilters.reference_date_to,
    activeFilters.categoria,
    activeFilters.curva !== undefined && activeFilters.curva !== null,
    activeFilters.unavailability_eligible !== undefined &&
      activeFilters.unavailability_eligible !== null,
    activeFilters.promo_eligible_flag !== undefined &&
      activeFilters.promo_eligible_flag !== null,
    activeFilters.atinge_zero_waste !== undefined &&
      activeFilters.atinge_zero_waste !== null
  ].filter(Boolean).length;

  const handleApplyFilters = filters => {
    setActiveFilters(filters);
    onChange({
      page: 1,
      latest_run: filters.latest_run,
      run_id: filters.run_id,
      reference_date_from: filters.reference_date_from,
      reference_date_to: filters.reference_date_to,
      categoria: filters.categoria,
      curva: filters.curva,
      unavailability_eligible: filters.unavailability_eligible,
      promo_eligible_flag: filters.promo_eligible_flag,
      atinge_zero_waste: filters.atinge_zero_waste
    });
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    onChange({
      page: 1,
      latest_run: true,
      run_id: undefined,
      reference_date_from: undefined,
      reference_date_to: undefined,
      categoria: undefined,
      curva: undefined,
      unavailability_eligible: undefined,
      promo_eligible_flag: undefined,
      atinge_zero_waste: undefined
    });
  };

  const handleSyncPromotions = useCallback(async () => {
    const runId = data[0]?.run;
    if (!runId) {
      addPopup({
        type: "warning",
        title: "Sem dados",
        description: "Nenhum run disponível para sincronizar."
      });
      return;
    }
    setIsSyncing(true);
    try {
      await pricingService.syncPromotions(runId);
      addPopup({
        type: "success",
        title: "Sincronização concluída",
        description: `Run ${runId} sincronizado com sucesso.`
      });
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.msg ||
        err?.message ||
        String(err);
      addPopup({
        type: "error",
        title: "Erro ao sincronizar promoções",
        description: typeof msg === "string" ? msg : JSON.stringify(msg)
      });
    } finally {
      setIsSyncing(false);
    }
  }, [addPopup, data, refresh]);

  const handleTogglePromo = useCallback((id, value) => {
    setEditedPromo(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleToggleAll = useCallback(
    value => {
      const updates = {};
      data.forEach(row => {
        updates[row.product_base_id] = value;
      });
      setEditedPromo(prev => ({ ...prev, ...updates }));
    },
    [data]
  );

  const allVisibleActive = useMemo(
    () =>
      data.length > 0 &&
      data.every(
        row => editedPromo[row.product_base_id] ?? row.in_promo ?? false
      ),
    [data, editedPromo]
  );

  const handleBulkUpdate = useCallback(async () => {
    if (pendingPromoCount === 0) return;
    setIsUpdating(true);
    try {
      const items = Object.entries(editedPromo).map(([id, isActive]) => ({
        product_base_id: parseInt(id, 10),
        is_active: isActive
      }));
      await pricingService.bulkUpdatePromocoes(items);
      addPopup({
        type: "success",
        title: "Promoções atualizadas",
        description: "Alterações salvas com sucesso."
      });
      setEditedPromo({});
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.msg ||
        err?.message ||
        String(err);
      addPopup({
        type: "error",
        title: "Erro ao atualizar promoções",
        description: typeof msg === "string" ? msg : JSON.stringify(msg)
      });
    } finally {
      setIsUpdating(false);
    }
  }, [addPopup, editedPromo, pendingPromoCount, refresh]);

  const getBoolBadge = (value, labelTrue = "Sim", labelFalse = "Não") => (
    <span
      className={`${classes.flagBadge} ${
        value ? classes.badgeSuccess : classes.badgeFail
      }`}
    >
      {value ? labelTrue : labelFalse}
    </span>
  );

  const getExcessoClass = pct => {
    if (pct >= 0.8) return classes.excessoHigh;
    if (pct >= 0.4) return classes.excessoMid;
    return classes.excessoLow;
  };

  const getCurvaBadge = curva => {
    if (!curva) return "--";
    let curvaClass = classes.badgeFail;
    if (curva === "A") curvaClass = classes.badgeSuccess;
    else if (curva === "B") curvaClass = classes.badgeWarning;
    return (
      <span className={`${classes.flagBadge} ${curvaClass}`}>{curva}</span>
    );
  };

  const columns = useMemo(
    () => [
      {
        field: "product_base_id",
        label: "ID",
        align: "center",
        style: { width: "54px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <Typography
            variant="caption"
            style={{ color: "#888", fontWeight: 600 }}
          >
            {row.product_base_id}
          </Typography>
        )
      },
      {
        field: "in_promo",
        label: "Ativar",
        disabledSort: true,
        align: "center",
        style: { width: "70px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const current =
            editedPromo[row.product_base_id] ?? row.in_promo ?? false;
          return (
            <Switch
              size="small"
              checked={current}
              color="primary"
              onChange={e =>
                handleTogglePromo(row.product_base_id, e.target.checked)
              }
            />
          );
        }
      },
      {
        field: "unavailability_eligible",
        label: "Indisp. eleg.",
        align: "center",
        style: { width: "96px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => getBoolBadge(row.unavailability_eligible)
      },
      {
        field: "product_base_name",
        label: "Produto",
        style: { minWidth: "140px", ...COMPACT_HEAD },
        cellStyle: compactCell
      },
      {
        field: "categoria",
        label: "Categoria",
        style: { minWidth: "90px", ...COMPACT_HEAD },
        cellStyle: compactCell
      },
      {
        field: "curva",
        label: "Curva",
        align: "center",
        style: { width: "62px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => getCurvaBadge(row.curva)
      },
      {
        field: "reference_date",
        label: "Data Ref.",
        align: "center",
        style: { width: "96px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatDate(value)
      },
      {
        field: "validade_estoque",
        label: "Validade",
        align: "center",
        style: { width: "96px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatDate(value)
      },
      {
        field: "stock_start",
        label: "Est. Ini.",
        align: "right",
        style: { width: "88px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatQty(value)
      },
      {
        field: "shelf_b",
        label: "Shelf B",
        align: "right",
        style: { width: "64px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <Typography variant="caption">
            {row.shelf_b != null ? `${row.shelf_b}d` : "--"}
          </Typography>
        )
      },
      {
        field: "demanda_validade",
        label: "Demanda/Val.",
        align: "right",
        style: { width: "100px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatQty(value)
      },
      {
        field: "excesso_qty",
        label: "Excesso",
        align: "right",
        style: { width: "88px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatQty(value)
      },
      {
        field: "excesso_pct",
        label: "% Exc.",
        align: "right",
        style: { width: "72px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <span className={getExcessoClass(row.excesso_pct)}>
            {formatPercent(row.excesso_pct)}
          </span>
        )
      },
      {
        field: "promo_eligible_flag",
        label: "Elegível",
        align: "center",
        style: { width: "80px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => getBoolBadge(row.promo_eligible_flag)
      },
      {
        field: "volume_projetado_no_preco",
        label: "Vol. Proj.",
        align: "right",
        style: { width: "84px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        formatter: value => formatQty(value)
      },
      {
        field: "sobra_projetada_ate_shelf",
        label: "Sobra Proj.",
        align: "right",
        style: { width: "90px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <span
            className={
              row.sobra_projetada_ate_shelf <= 0
                ? classes.sobraZeroOrNegative
                : classes.sobraPositive
            }
          >
            {formatQty(row.sobra_projetada_ate_shelf)}
          </span>
        )
      },
      {
        field: "atinge_zero_waste_flag",
        label: "Zero Waste",
        align: "center",
        style: { width: "90px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) =>
          getBoolBadge(row.atinge_zero_waste_flag, "Atingido", "Não atingido")
      },
      {
        field: "promo_reason",
        label: "Motivo",
        style: { minWidth: "180px", ...COMPACT_HEAD },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <span className={classes.promoReason}>
            {row.promo_reason || "--"}
          </span>
        )
      }
    ],
    [classes, editedPromo, handleTogglePromo]
  );

  return (
    <>
      <ContainerPage>
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{ gap: "1rem", marginBottom: "0.5rem" }}
        >
          <TitlePage>Sugestões de Promoção</TitlePage>
        </Box>

        <Table
          columns={columns}
          data={data}
          onChange={onChange}
          params={params}
          status={status}
          refresh={refresh}
          count={count}
          paginationServer
          handleSearch={handleSearch}
          placeholder="Produto..."
          size="small"
          isMobile={false}
          rowStyle={row =>
            editedPromo[row.product_base_id] !== undefined
              ? { backgroundColor: "#fffde7" }
              : {}
          }
          filters={
            <>
              <Tooltip title="Filtrar dados" arrow>
                <IconButton
                  onClick={() => setIsFilterDrawerOpen(true)}
                  centerRipple={false}
                  style={{
                    height: "56px",
                    width: "56px",
                    border: `2px solid ${
                      activeFiltersCount > 0
                        ? theme.palette.primary.main
                        : "#e0e0e0"
                    }`,
                    borderRadius: "4px",
                    backgroundColor:
                      activeFiltersCount > 0 ? "#e3f2fd" : "#fff",
                    transition: "all 0.2s ease-in-out"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor =
                      theme.palette.primary.dark;
                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor =
                      activeFiltersCount > 0
                        ? theme.palette.primary.main
                        : "#e0e0e0";
                    e.currentTarget.style.backgroundColor =
                      activeFiltersCount > 0 ? "#e3f2fd" : "#fff";
                  }}
                >
                  <FilterListIcon
                    style={{
                      fontSize: "24px",
                      color:
                        activeFiltersCount > 0
                          ? theme.palette.primary.main
                          : "#666"
                    }}
                  />
                </IconButton>
              </Tooltip>

              <Box
                display="flex"
                alignItems="center"
                style={{ gap: "0.5rem", marginLeft: "auto" }}
              >
                {activeFiltersCount > 0 && (
                  <Typography
                    variant="body2"
                    style={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      whiteSpace: "nowrap"
                    }}
                  >
                    • {activeFiltersCount} filtro(s) ativo(s)
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  onClick={handleSyncPromotions}
                  disabled={isSyncing || data.length === 0}
                >
                  {isSyncing ? "Sincronizando..." : "Sincronizar Promoções"}
                </Button>
                <Tooltip title="Ativar/desativar todos visíveis" arrow>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <Switch
                      size="small"
                      checked={allVisibleActive}
                      color="primary"
                      onChange={e => handleToggleAll(e.target.checked)}
                    />
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: "nowrap", color: "#555" }}
                    >
                      Ativar todos
                    </Typography>
                  </Box>
                </Tooltip>
                {pendingPromoCount > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setEditedPromo({})}
                    disabled={isUpdating}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleBulkUpdate}
                  disabled={pendingPromoCount === 0 || isUpdating}
                >
                  Atualizar promoções
                  {pendingPromoCount > 0 ? ` (${pendingPromoCount})` : ""}
                </Button>
              </Box>
            </>
          }
        />
      </ContainerPage>

      <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        initialParams={activeFilters}
        categories={categoryOptions}
      />
    </>
  );
};

export default PromoSuggestions;
